<template>
  <div>
    <page-loading v-if="loading" message="Carregando financeiro" />
    <template v-else>
      <section class="mb-4">
        <h2 class="br-section-title">Resumo</h2>
        <v-row dense>
          <v-col cols="12" sm="6" lg="3">
            <partner-summary-metric-card
              title="A pagar (pendente)"
              :value="formatPrice(summary.payables.pendingTotal)"
              icon="mdi-arrow-up-circle-outline"
              variant="brand"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <partner-summary-metric-card
              title="A pagar (vencido)"
              :value="formatPrice(summary.payables.overdueTotal)"
              icon="mdi-alert-circle-outline"
              variant="amber"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <partner-summary-metric-card
              title="A receber (pendente)"
              :value="formatPrice(summary.receivables.pendingTotal)"
              icon="mdi-arrow-down-circle-outline"
              variant="finance"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <partner-summary-metric-card
              title="A receber (vencido)"
              :value="formatPrice(summary.receivables.overdueTotal)"
              icon="mdi-clock-alert-outline"
              variant="amber"
            />
          </v-col>
        </v-row>
      </section>

      <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="finance-tabs">
        <v-tab>Contas a pagar</v-tab>
        <v-tab>Contas a receber</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="mt-4">
        <v-tab-item>
          <finance-entry-tab
            entry-type="payable"
            :entries="payables"
            :loading-entries="loadingEntries"
            :status-filter="statusFilter"
            :search="search"
            counterparty-label="Fornecedor"
            @load="loadPayables"
            @open-form="openForm('payable', $event)"
            @mark-paid="markPaid('payable', $event)"
            @cancel="cancelEntry('payable', $event)"
            @update:statusFilter="statusFilter = $event"
            @update:search="search = $event"
            @new="openForm('payable')"
          />
        </v-tab-item>

        <v-tab-item>
          <finance-entry-tab
            entry-type="receivable"
            :entries="receivables"
            :loading-entries="loadingEntries"
            :status-filter="statusFilter"
            :search="search"
            counterparty-label="Cliente"
            @load="loadReceivables"
            @open-form="openForm('receivable', $event)"
            @mark-paid="markPaid('receivable', $event)"
            @cancel="cancelEntry('receivable', $event)"
            @update:statusFilter="statusFilter = $event"
            @update:search="search = $event"
            @new="openForm('receivable')"
          />
        </v-tab-item>
      </v-tabs-items>

      <app-modal
        v-model="formDialog"
        :title="editingEntry ? 'Editar lançamento' : formTitle"
        icon="mdi-cash-plus"
        :max-width="560"
        persistent
      >
        <v-form ref="entryForm" v-model="formValid">
          <form-section title="Dados do lançamento" compact>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="form.description"
                  label="Descrição *"
                  outlined
                  dense
                  class="br-form-field"
                  :rules="[v => !!v || 'Obrigatório']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.counterpartyName"
                  :label="`${counterpartyLabel} *`"
                  outlined
                  dense
                  class="br-form-field"
                  :rules="[v => !!v || 'Obrigatório']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.amount"
                  label="Valor *"
                  outlined
                  dense
                  class="br-form-field"
                  v-money="money"
                  :rules="[v => !!v || 'Obrigatório']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.dueDate"
                  label="Vencimento *"
                  type="date"
                  outlined
                  dense
                  class="br-form-field"
                  :rules="[v => !!v || 'Obrigatório']"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.notes"
                  label="Observações"
                  outlined
                  dense
                  rows="2"
                  class="br-form-field"
                />
              </v-col>
            </v-row>
          </form-section>
        </v-form>
        <template #actions>
          <v-btn text @click="closeForm">Cancelar</v-btn>
          <v-btn dark color="primary" depressed :loading="saving" :disabled="!formValid" @click="saveEntry">
            Salvar
          </v-btn>
        </template>
      </app-modal>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PartnerSummaryMetricCard from "@/components/summary/PartnerSummaryMetricCard.vue";
import AppModal from "@/components/ui/AppModal.vue";
import FormSection from "@/components/ui/FormSection.vue";
import FinanceEntryTab from "@/components/finance/FinanceEntryTab.vue";
import { financeApi } from "@/services/finance/financeApi";
import {
  FinanceDashboardSummary,
  FinanceEntryType,
} from "@/types/finance/Finance";

type EntryKind = "payable" | "receivable";

export default defineComponent({
  components: { AppModal, FormSection, FinanceEntryTab, PageLoading, PartnerSummaryMetricCard },
  data() {
    return {
      loading: true,
      loadingEntries: false,
      saving: false,
      activeTab: 0,
      statusFilter: "",
      search: "",
      payables: [] as FinanceEntryType[],
      receivables: [] as FinanceEntryType[],
      summary: {
        payables: { pendingTotal: 0, overdueTotal: 0, paidThisMonthTotal: 0 },
        receivables: { pendingTotal: 0, overdueTotal: 0, paidThisMonthTotal: 0 },
      } as FinanceDashboardSummary,
      formDialog: false,
      formValid: false,
      formKind: "payable" as EntryKind,
      editingEntry: null as FinanceEntryType | null,
      form: {
        description: "",
        counterpartyName: "",
        amount: "R$ 0,00",
        dueDate: "",
        notes: "",
      },
      money: { decimal: ",", thousands: ".", prefix: "R$ ", precision: 2 },
    };
  },
  computed: {
    formTitle(): string {
      return this.formKind === "payable" ? "Nova conta a pagar" : "Nova conta a receber";
    },
    counterpartyLabel(): string {
      return this.formKind === "payable" ? "Fornecedor" : "Cliente";
    },
  },
  watch: {
    activeTab() {
      this.statusFilter = "";
      this.search = "";
      this.loadActiveTab();
    },
    "$route.query.tab"(tab) {
      if (tab === "receber") this.activeTab = 1;
      else if (tab === "pagar") this.activeTab = 0;
    },
  },
  async mounted() {
    if (this.$route.query.tab === "receber") this.activeTab = 1;
    await this.loadAll();
    this.loading = false;
  },
  methods: {
    async loadAll() {
      await Promise.all([this.loadSummary(), this.loadActiveTab()]);
    },
    async loadSummary() {
      try {
        this.summary = await financeApi.getSummary();
      } catch {
        this.summary = {
          payables: { pendingTotal: 0, overdueTotal: 0, paidThisMonthTotal: 0 },
          receivables: { pendingTotal: 0, overdueTotal: 0, paidThisMonthTotal: 0 },
        };
      }
    },
    loadActiveTab() {
      if (this.activeTab === 0) return this.loadPayables();
      return this.loadReceivables();
    },
    async loadPayables() {
      this.loadingEntries = true;
      try {
        const result = await financeApi.listPayables({
          status: this.statusFilter || undefined,
          search: this.search || undefined,
          limit: 100,
        });
        this.payables = result.data || [];
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar contas a pagar",
          color: "error",
          timer: 3000,
        });
      } finally {
        this.loadingEntries = false;
      }
    },
    async loadReceivables() {
      this.loadingEntries = true;
      try {
        const result = await financeApi.listReceivables({
          status: this.statusFilter || undefined,
          search: this.search || undefined,
          limit: 100,
        });
        this.receivables = result.data || [];
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar contas a receber",
          color: "error",
          timer: 3000,
        });
      } finally {
        this.loadingEntries = false;
      }
    },
    formatPrice(value: number) {
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    parseMoney(value: string) {
      return Number(value.replace("R$", "").replace(/\./g, "").replace(",", ".").trim()) || 0;
    },
    openForm(kind: EntryKind, entry?: FinanceEntryType) {
      this.formKind = kind;
      this.editingEntry = entry || null;
      if (entry) {
        Object.assign(this.form, {
          description: entry.description,
          counterpartyName: entry.counterpartyName,
          amount: this.formatPrice(entry.amount),
          dueDate: entry.dueDate,
          notes: entry.notes || "",
        });
      } else {
        Object.assign(this.form, {
          description: "",
          counterpartyName: "",
          amount: "R$ 0,00",
          dueDate: new Date().toISOString().slice(0, 10),
          notes: "",
        });
      }
      this.formDialog = true;
    },
    closeForm() {
      this.formDialog = false;
      this.editingEntry = null;
    },
    async saveEntry() {
      this.saving = true;
      const payload = {
        description: this.form.description,
        counterpartyName: this.form.counterpartyName,
        amount: this.parseMoney(this.form.amount),
        dueDate: this.form.dueDate,
        notes: this.form.notes || undefined,
      };
      try {
        if (this.editingEntry) {
          if (this.formKind === "payable") {
            await financeApi.updatePayable(this.editingEntry.id, payload);
          } else {
            await financeApi.updateReceivable(this.editingEntry.id, payload);
          }
        } else if (this.formKind === "payable") {
          await financeApi.createPayable(payload);
        } else {
          await financeApi.createReceivable(payload);
        }
        this.closeForm();
        await this.loadAll();
        this.$store.dispatch("showSnack", {
          message: "Lançamento salvo",
          color: "success",
          timer: 3000,
        });
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao salvar lançamento",
          color: "error",
          timer: 3000,
        });
      } finally {
        this.saving = false;
      }
    },
    async markPaid(kind: EntryKind, entry: FinanceEntryType) {
      try {
        if (kind === "payable") {
          await financeApi.markPayablePaid(entry.id);
        } else {
          await financeApi.markReceivablePaid(entry.id);
        }
        await this.loadAll();
        this.$store.dispatch("showSnack", {
          message: kind === "payable" ? "Conta marcada como paga" : "Conta marcada como recebida",
          color: "success",
          timer: 3000,
        });
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao atualizar status",
          color: "error",
          timer: 3000,
        });
      }
    },
    async cancelEntry(kind: EntryKind, entry: FinanceEntryType) {
      try {
        if (kind === "payable") {
          await financeApi.cancelPayable(entry.id);
        } else {
          await financeApi.cancelReceivable(entry.id);
        }
        await this.loadAll();
        this.$store.dispatch("showSnack", {
          message: "Lançamento cancelado",
          color: "success",
          timer: 3000,
        });
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao cancelar lançamento",
          color: "error",
          timer: 3000,
        });
      }
    },
  },
});
</script>
